import React, { useEffect, useState } from "react";
import CrudTable from "@/components/common/CrudTable";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Factory, Building2, Layers, ListChecks } from "lucide-react";

function SuppliersTab({ canWrite }) {
  const [plants, setPlants] = useState([]);
  useEffect(() => { api.get("/plants").then(({ data }) => setPlants(data)); }, []);
  const fields = [
    { key: "code", label: "Code", required: true },
    { key: "name", label: "Name", required: true },
    { key: "category", label: "Category", type: "select", options: [
      { value: "Mechanical", label: "Mechanical" },
      { value: "Electrical", label: "Electrical" },
      { value: "Metal", label: "Metal" },
      { value: "Plastics", label: "Plastics" },
      { value: "Rubber", label: "Rubber" },
      { value: "Tooling", label: "Tooling" },
      { value: "Chemical", label: "Chemical" },
    ]},
    { key: "plant_id", label: "Assigned Plant", type: "select", options: plants.map((p) => ({ value: p.id, label: p.name })) },
    { key: "pic_name", label: "PIC Name" },
    { key: "pic_email", label: "PIC Email" },
    { key: "pic_phone", label: "PIC Phone" },
    { key: "address", label: "Address", type: "textarea" },
  ];
  return (
    <CrudTable
      title="Suppliers" subtitle="Manage supplier master data" endpoint="/suppliers"
      fields={fields}
      canWrite={canWrite} canDelete={canWrite}
      columns={[
        { key: "code", label: "Code", className: "font-mono text-xs" },
        { key: "name", label: "Name", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
        { key: "category", label: "Category" },
        { key: "plant_id", label: "Plant", render: (r) => plants.find((p) => p.id === r.plant_id)?.name || <span className="text-slate-400">—</span> },
        { key: "pic_name", label: "PIC" },
        { key: "pic_email", label: "PIC Email", className: "font-mono text-xs" },
      ]}
      testIdPrefix="supplier"
    />
  );
}

function QuestionsTab({ canWrite }) {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);
  return (
    <CrudTable
      title="Audit Questions" subtitle="Checklist questions per category" endpoint="/questions"
      canWrite={canWrite} canDelete={canWrite}
      fields={[
        { key: "category_id", label: "Category", type: "select", options: cats.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })), required: true },
        { key: "question", label: "Question", type: "textarea", required: true },
        { key: "weight", label: "Weight", type: "number", default: 1 },
        { key: "max_score", label: "Max Score", type: "number", default: 5 },
      ]}
      columns={[
        { key: "category_id", label: "Category", render: (r) => {
          const c = cats.find((c) => c.id === r.category_id);
          return c ? `${c.code} — ${c.name}` : "—";
        }},
        { key: "question", label: "Question", render: (r) => <span className="text-slate-800">{r.question}</span> },
        { key: "weight", label: "Weight", className: "font-mono text-xs" },
        { key: "max_score", label: "Max Score", className: "font-mono text-xs" },
      ]}
      testIdPrefix="question"
    />
  );
}

export default function SuppliersPage() {
  const { user } = useAuth();
  const canWrite = hasRole(user, "sd_admin"); // system_admin also passes via hasRole
  const [tab, setTab] = useState("suppliers");

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Master Data</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Supplier Management</h1>
        <p className="text-sm text-slate-600 mt-1">Manage suppliers, manufacturing plants, and the audit checklist framework.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 rounded-md p-1">
          <TabsTrigger value="suppliers" data-testid="tab-suppliers" className="gap-2">
            <Factory size={13} /> Suppliers
          </TabsTrigger>
          <TabsTrigger value="plants" data-testid="tab-plants" className="gap-2">
            <Building2 size={13} /> Plants
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories" className="gap-2">
            <Layers size={13} /> Audit Categories
          </TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions" className="gap-2">
            <ListChecks size={13} /> Questions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="mt-4">
          <SuppliersTab canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="plants" className="mt-4">
          <CrudTable
            title="Plants" subtitle="Manufacturing plants / facilities" endpoint="/plants"
            canWrite={canWrite} canDelete={canWrite}
            fields={[
              { key: "code", label: "Code", required: true },
              { key: "name", label: "Name", required: true },
              { key: "location", label: "Location", required: true },
            ]}
            columns={[
              { key: "code", label: "Code", className: "font-mono text-xs" },
              { key: "name", label: "Name", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
              { key: "location", label: "Location" },
            ]}
            testIdPrefix="plant"
          />
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <CrudTable
            title="Audit Categories" subtitle="Question categories with weighting" endpoint="/categories"
            canWrite={canWrite} canDelete={canWrite}
            fields={[
              { key: "code", label: "Code", required: true },
              { key: "name", label: "Name", required: true },
              { key: "description", label: "Description", type: "textarea" },
              { key: "weight", label: "Weight", type: "number", default: 1 },
            ]}
            columns={[
              { key: "code", label: "Code", className: "font-mono text-xs" },
              { key: "name", label: "Name", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
              { key: "description", label: "Description" },
              { key: "weight", label: "Weight", className: "font-mono text-xs" },
            ]}
            testIdPrefix="category"
          />
        </TabsContent>

        <TabsContent value="questions" className="mt-4">
          <QuestionsTab canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
